import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertCircle, GraduationCap, Loader2, UserPlus2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type LearnerFormCustomField,
  type LearnerFormResponse,
  type LearnerFormStandardField,
  type LearningContentLanguage,
  type Student,
  type StudentCreateInput,
  type StudentGender,
  type StudentLearningOverridesInput,
  type StudentNepStage,
  type StudentUpdateInput,
  type TenantVerticalLiteral,
} from "@eduviaos/shared";
import { useLearnerForm } from "@/hooks/useLearnerForm";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { useAIDraftGenerator, type LearnerDraft } from "@/lib/aiDraft";
import { useCreateStudent, useUpdateStudent } from "@/hooks/useStudents";
import { useTerms } from "@/hooks/useTerms";
import { defaultLearnerForm } from "@/lib/learnerFormDefaults";
import {
  customDraftKey,
  validateLearnerDraft,
  type LearnerFormErrors,
} from "@/lib/learnerFormValidation";

/**
 * Learner create / edit sheet (Phase J1.b, config-driven since SC-22).
 *
 * The fields, their order, labels and required flags come from the
 * tenant's resolved learner form (`GET /me/learner-form`), so a study
 * centre never sees Grade / Section / NEP stage / Parent unless it turns
 * them on. Validation runs client-side from the same config (zod) and the
 * backend re-applies it on save.
 *
 * Status is intentionally not editable here — transitions go through
 * `<StudentTransitionDialog>` so the audit + lifecycle side-effects stay
 * consistent.
 */

const GENDER_OPTIONS: ReadonlyArray<{ value: StudentGender; label: string }> = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non_binary", label: "Non-binary" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
  { value: "other", label: "Other" },
];

const NEP_STAGE_OPTIONS: ReadonlyArray<{ value: StudentNepStage; label: string; range: string }> = [
  { value: "foundational", label: "Foundational", range: "Ages 3–8" },
  { value: "preparatory", label: "Preparatory", range: "Ages 8–11" },
  { value: "middle", label: "Middle", range: "Ages 11–14" },
  { value: "secondary", label: "Secondary", range: "Ages 14–18" },
];

const PLACEHOLDERS: Record<string, string> = {
  name: "Aarav Sharma",
  email: "aarav@example.com",
  phone: "+91 90000 00000",
  roll_number: "SR-101",
  admission_number: "ADM/2026/0001",
  parent_name: "Anita Sharma",
  parent_email: "anita@example.com",
  parent_phone: "+91 90000 00001",
  grade: "5",
  section: "A",
  blood_group: "O+",
  primary_language: "en-IN",
};

const DEFAULT_HELP: Record<string, string> = {
  parent_name: "Shown on the roster and used to link siblings.",
  aadhaar_reference: "Tokenized reference, not the raw number. Visible only to admins.",
};

/** Columns the update endpoint does not accept; shown read-only when editing. */
const CREATE_ONLY_KEYS: ReadonlySet<string> = new Set(["admission_number"]);

interface LearningDraft {
  max_questions: string;
  default_questions: string;
  preferred_language: LearningContentLanguage | "";
  ziggy_variant: "pink" | "blue" | "";
}

interface DraftState {
  values: Record<string, string>;
  custom: Record<string, string>;
  learning: LearningDraft;
}

function asString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

function storedValue(field: LearnerFormStandardField, existing: Student): string {
  if (field.storage === "metadata") return asString(existing.metadata?.[field.key]);
  const record = existing as unknown as Record<string, unknown>;
  return asString(record[field.key]);
}

function storedCustom(existing: Student | null | undefined): Record<string, unknown> {
  const raw = existing?.metadata?.custom;
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function draftFromExisting(
  form: LearnerFormResponse,
  existing: Student | null | undefined,
): DraftState {
  const values: Record<string, string> = {};
  for (const field of form.fields) {
    values[field.key] = existing ? storedValue(field, existing) : "";
  }
  if (!values.primary_language) values.primary_language = "en-IN";
  const customStored = storedCustom(existing);
  const custom: Record<string, string> = {};
  for (const field of form.custom_fields) custom[field.key] = asString(customStored[field.key]);
  const overrides = existing?.learning_overrides;
  return {
    values,
    custom,
    learning: {
      max_questions:
        overrides?.max_questions_per_quiz != null ? String(overrides.max_questions_per_quiz) : "",
      default_questions:
        overrides?.default_question_count != null ? String(overrides.default_question_count) : "",
      preferred_language: overrides?.preferred_language ?? "",
      ziggy_variant:
        overrides?.ziggy_variant === "pink" || overrides?.ziggy_variant === "blue"
          ? overrides.ziggy_variant
          : "",
    },
  };
}

function parseOptionalInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function buildLearningOverrides(learning: LearningDraft): StudentLearningOverridesInput | null {
  const maxQ = parseOptionalInt(learning.max_questions);
  const defaultQ = parseOptionalInt(learning.default_questions);
  const lang = learning.preferred_language || null;
  const ziggyVariant = learning.ziggy_variant || null;
  if (maxQ == null && defaultQ == null && lang == null && ziggyVariant == null) return null;
  return {
    max_questions_per_quiz: maxQ,
    default_question_count: defaultQ,
    preferred_language: lang,
    ziggy_variant: ziggyVariant,
  };
}

function trimmedOrNull(value: string | undefined): string | null {
  const t = (value ?? "").trim();
  return t.length > 0 ? t : null;
}

function customPayloadValue(field: LearnerFormCustomField, raw: string): string | number | null {
  const value = raw.trim();
  if (!value) return null;
  return field.type === "number" ? Number(value) : value;
}

function buildCustom(
  form: LearnerFormResponse,
  draft: DraftState,
  base: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...base };
  for (const field of form.custom_fields) {
    if (!field.visible) continue;
    const value = customPayloadValue(field, draft.custom[field.key] ?? "");
    if (value === null) delete next[field.key];
    else next[field.key] = value;
  }
  return next;
}

function setColumn(
  payload: StudentCreateInput | StudentUpdateInput,
  key: string,
  value: string | null,
): void {
  const record = payload as Record<string, unknown>;
  record[key] = value;
}

function buildCreatePayload(form: LearnerFormResponse, draft: DraftState): StudentCreateInput {
  const payload: StudentCreateInput = { name: (draft.values.name ?? "").trim() };
  const metadata: Record<string, unknown> = {};
  for (const field of form.fields) {
    if (!field.visible || field.key === "name") continue;
    const value = trimmedOrNull(draft.values[field.key]);
    if (!value) continue;
    if (field.storage === "metadata") metadata[field.key] = value;
    else setColumn(payload, field.key, value);
  }
  const custom = buildCustom(form, draft, {});
  if (Object.keys(custom).length > 0) metadata.custom = custom;
  if (Object.keys(metadata).length > 0) payload.metadata = metadata;
  const overrides = buildLearningOverrides(draft.learning);
  if (overrides) payload.learning_overrides = overrides;
  return payload;
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function buildUpdatePayload(
  form: LearnerFormResponse,
  draft: DraftState,
  existing: Student,
): StudentUpdateInput {
  const payload: StudentUpdateInput = {};
  const existingMetadata: Record<string, unknown> = { ...(existing.metadata ?? {}) };
  const metadata: Record<string, unknown> = { ...existingMetadata };
  for (const field of form.fields) {
    if (!field.visible || CREATE_ONLY_KEYS.has(field.key)) continue;
    const raw = draft.values[field.key];
    if (field.key === "name") {
      const name = (raw ?? "").trim();
      if (name && name !== existing.name) payload.name = name;
      continue;
    }
    if (field.storage === "metadata") {
      const value = trimmedOrNull(raw);
      if (value) metadata[field.key] = value;
      else delete metadata[field.key];
      continue;
    }
    const value =
      field.key === "primary_language" ? (trimmedOrNull(raw) ?? "en-IN") : trimmedOrNull(raw);
    if ((storedValue(field, existing) || null) !== value) setColumn(payload, field.key, value);
  }
  const custom = buildCustom(form, draft, storedCustom(existing));
  if (Object.keys(custom).length > 0) metadata.custom = custom;
  else delete metadata.custom;
  if (!sameJson(metadata, existingMetadata)) payload.metadata = metadata;

  const overrides = buildLearningOverrides(draft.learning);
  if (overrides) payload.learning_overrides = overrides;
  return payload;
}

export interface StudentEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: Student | null;
  onSaved?: (saved: Student) => void;
  vertical?: TenantVerticalLiteral;
}

export function StudentEditorSheet({
  open,
  onOpenChange,
  existing,
  onSaved,
  vertical,
}: StudentEditorSheetProps) {
  const isEdit = Boolean(existing);
  const reduceMotion = useReducedMotion();
  const { terms, ct, profile, vertical: resolvedVertical } = useTerms(vertical);
  const personOne = terms.learner.one.toLowerCase();
  const personMany = terms.learner.many.toLowerCase();

  const formQuery = useLearnerForm({ enabled: open });
  const fallbackForm = React.useMemo(
    () => defaultLearnerForm(resolvedVertical),
    [resolvedVertical],
  );
  const form = formQuery.data ?? (formQuery.isError ? fallbackForm : null);

  const [draft, setDraft] = React.useState<DraftState>(() =>
    draftFromExisting(form ?? fallbackForm, existing),
  );
  const [errors, setErrors] = React.useState<LearnerFormErrors>({});
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open && form) {
      setDraft(draftFromExisting(form, existing));
      setErrors({});
      setSubmitError(null);
    }
  }, [open, existing, form]);

  const createMutation = useCreateStudent();
  const updateMutation = useUpdateStudent();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const trimmedName = (draft.values.name ?? "").trim();
  const canSubmit = form !== null && trimmedName.length > 0 && !isSubmitting;

  const setValue = (key: string, value: string) => {
    setDraft((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }));
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setCustom = (key: string, value: string) => {
    setDraft((prev) => ({ ...prev, custom: { ...prev.custom, [key]: value } }));
    const errorKey = customDraftKey(key);
    setErrors((prev) => {
      if (!(errorKey in prev)) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  const generateLearnerDraft = useAIDraftGenerator("learner");
  const describeLearnerDraft = React.useCallback(
    (next: LearnerDraft) => {
      if (!form) return [];
      const rows: { label: string; value: string }[] = [];
      for (const field of form.fields) {
        const value = next.fields[field.key];
        if (field.visible && value) rows.push({ label: field.label, value });
      }
      for (const field of form.custom_fields) {
        const value = next.custom_fields[field.key];
        if (field.visible && value) rows.push({ label: field.label, value });
      }
      return rows;
    },
    [form],
  );
  const applyLearnerDraft = React.useCallback(
    (next: LearnerDraft) => {
      if (!form) return;
      const visibleKeys = new Set(form.fields.filter((f) => f.visible).map((f) => f.key));
      const visibleCustomKeys = new Set(
        form.custom_fields.filter((f) => f.visible).map((f) => f.key),
      );
      setDraft((prev) => {
        const values = { ...prev.values };
        for (const [key, value] of Object.entries(next.fields)) {
          if (visibleKeys.has(key) && value) values[key] = value;
        }
        const custom = { ...prev.custom };
        for (const [key, value] of Object.entries(next.custom_fields)) {
          if (visibleCustomKeys.has(key) && value) custom[key] = value;
        }
        return { ...prev, values, custom };
      });
      setErrors({});
      setSubmitError(null);
    },
    [form],
  );

  const setLearning = <K extends keyof LearningDraft>(key: K, value: LearningDraft[K]) => {
    setDraft((prev) => ({ ...prev, learning: { ...prev.learning, [key]: value } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit || !form) return;

    const found = validateLearnerDraft(form, draft.values, draft.custom, {
      readOnlyKeys: existing ? CREATE_ONLY_KEYS : undefined,
    });
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setSubmitError("Check the highlighted fields.");
      return;
    }

    try {
      if (existing) {
        const body = buildUpdatePayload(form, draft, existing);
        if (Object.keys(body).length === 0) {
          onOpenChange(false);
          return;
        }
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        onSaved?.(saved);
        toast.success(`Saved changes to ${saved.name}.`);
        onOpenChange(false);
      } else {
        const body = buildCreatePayload(form, draft);
        const saved = await createMutation.mutateAsync(body);
        onSaved?.(saved);
        toast.success(`Added ${saved.name} to the roster.`);
        onOpenChange(false);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const field = typeof err.details?.field === "string" ? err.details.field : null;
        if (
          field &&
          (err.details?.code === "student.field_required" ||
            err.details?.code === "student.custom_field_invalid")
        ) {
          const isCustom = form.custom_fields.some((c) => c.key === field);
          setErrors((prev) => ({
            ...prev,
            [isCustom ? customDraftKey(field) : field]: err.message,
          }));
        }
        setSubmitError(friendlyApiMessage(err, personOne, ct));
      } else {
        setSubmitError(
          err instanceof Error ? err.message : `Couldn't save the ${personOne}. Please retry.`,
        );
      }
    }
  };

  const title = isEdit ? `Edit ${personOne}` : `Add ${personOne}`;
  const description = isEdit
    ? `Update ${existing?.name ?? `this ${personOne}`}'s profile. Changes are audited.`
    : `Add a single ${personOne} to your ${profile.institutionNoun.toLowerCase()}. For bulk imports use the Import ${personMany} page.`;

  const visibleFields = form ? form.fields.filter((f) => f.visible) : [];
  const visibleCustom = form ? form.custom_fields.filter((f) => f.visible) : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[540px]"
        data-testid="student-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              {isEdit ? <GraduationCap className="size-5" /> : <UserPlus2 className="size-5" />}
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">{title}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">{description}</SheetDescription>
              {isEdit ? null : (
                <div className="pt-3">
                  <AIDraftButton<LearnerDraft>
                    title={`Draft a ${personOne}`}
                    placeholder="e.g. Priya Nair, priya@example.com, 98765 43210, preparing for CA Foundation"
                    generate={generateLearnerDraft}
                    describe={describeLearnerDraft}
                    onApply={applyLearnerDraft}
                    disabled={!form || isSubmitting}
                    data-testid="student-ai-draft"
                  />
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex min-h-0 flex-1 flex-col"
          data-testid="student-editor-form"
        >
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <AnimatePresence>
              {submitError ? (
                <motion.div
                  initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                  transition={{ duration: 0.16, ease: "easeOut" }}
                  role="alert"
                  className="border-destructive/30 bg-destructive/5 text-destructive mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
                >
                  <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                  <span className="flex-1">{submitError}</span>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {form === null ? (
              <div className="space-y-4" data-testid="student-editor-loading" aria-busy="true">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="bg-muted h-3 w-24 animate-pulse rounded motion-reduce:animate-none" />
                    <div className="bg-muted h-9 w-full animate-pulse rounded-md motion-reduce:animate-none" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <Section
                  title="Details"
                  subtitle={
                    visibleFields.some((f) => f.required && !f.locked)
                      ? "Fields marked * are required."
                      : "Name is required; the rest is optional."
                  }
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {visibleFields.map((field) => (
                      <StandardFieldInput
                        key={field.key}
                        field={field}
                        value={draft.values[field.key] ?? ""}
                        error={errors[field.key]}
                        readOnly={isEdit && CREATE_ONLY_KEYS.has(field.key)}
                        onChange={(value) => setValue(field.key, value)}
                      />
                    ))}
                  </div>
                </Section>

                {visibleCustom.length > 0 ? (
                  <Section title="More details" subtitle="Fields your institution added.">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {visibleCustom.map((field) => (
                        <CustomFieldInput
                          key={field.key}
                          field={field}
                          value={draft.custom[field.key] ?? ""}
                          error={errors[customDraftKey(field.key)]}
                          onChange={(value) => setCustom(field.key, value)}
                        />
                      ))}
                    </div>
                  </Section>
                ) : null}

                <Section
                  title="Learning overrides"
                  subtitle="Optional caps for the learner app. Cannot exceed institution defaults."
                >
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Max questions per quiz" htmlFor="student-learning-max">
                      <Input
                        id="student-learning-max"
                        inputMode="numeric"
                        value={draft.learning.max_questions}
                        onChange={(e) => setLearning("max_questions", e.target.value)}
                        placeholder="Uses centre default"
                      />
                    </Field>
                    <Field label="Default question count" htmlFor="student-learning-default">
                      <Input
                        id="student-learning-default"
                        inputMode="numeric"
                        value={draft.learning.default_questions}
                        onChange={(e) => setLearning("default_questions", e.target.value)}
                        placeholder="Uses centre default"
                      />
                    </Field>
                  </div>
                  <Field label="Preferred AI language" htmlFor="student-learning-lang">
                    <Select
                      value={draft.learning.preferred_language || "__default__"}
                      onValueChange={(v) =>
                        setLearning(
                          "preferred_language",
                          v === "__default__" ? "" : (v as LearningContentLanguage),
                        )
                      }
                    >
                      <SelectTrigger id="student-learning-lang">
                        <SelectValue placeholder="Centre default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Centre default</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Ziggy mascot variant" htmlFor="student-learning-ziggy">
                    <Select
                      value={draft.learning.ziggy_variant || "__default__"}
                      onValueChange={(v) =>
                        setLearning(
                          "ziggy_variant",
                          v === "__default__" ? "" : (v as LearningDraft["ziggy_variant"]),
                        )
                      }
                    >
                      <SelectTrigger id="student-learning-ziggy">
                        <SelectValue placeholder="Centre default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Centre default</SelectItem>
                        <SelectItem value="pink">Pink Ziggy</SelectItem>
                        <SelectItem value="blue">Blue Ziggy</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </Section>
              </>
            )}
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              {isEdit ? "Save changes" : `Add ${personOne}`}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function fieldId(key: string): string {
  return `student-field-${key.replace(/_/g, "-")}`;
}

interface StandardFieldInputProps {
  field: LearnerFormStandardField;
  value: string;
  error: string | undefined;
  readOnly: boolean;
  onChange: (value: string) => void;
}

function StandardFieldInput({ field, value, error, readOnly, onChange }: StandardFieldInputProps) {
  const id = fieldId(field.key);
  const errorId = `${id}-error`;
  const wide = field.key === "name" || field.kind === "textarea";
  const helper = readOnly
    ? "Set when the record was created."
    : (field.help_text ?? DEFAULT_HELP[field.key]);
  const common = {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? errorId : undefined,
  } as const;

  let control: React.ReactNode;
  if (field.key === "gender") {
    control = (
      <Select
        value={value || "__none__"}
        onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
      >
        <SelectTrigger {...common}>
          <SelectValue placeholder="Not specified" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Not specified</SelectItem>
          {GENDER_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  } else if (field.key === "stage") {
    control = (
      <Select
        value={value || "__none__"}
        onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
      >
        <SelectTrigger {...common}>
          <SelectValue placeholder="Not specified" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Not specified</SelectItem>
          {NEP_STAGE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              <span className="flex items-center gap-2">
                <span>{opt.label}</span>
                <span className="text-muted-foreground text-xs">{opt.range}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  } else if (field.kind === "textarea") {
    control = (
      <Textarea
        {...common}
        value={value}
        rows={2}
        maxLength={field.key === "aadhaar_reference" ? 80 : 500}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    const type =
      field.kind === "email"
        ? "email"
        : field.kind === "phone"
          ? "tel"
          : field.kind === "date"
            ? "date"
            : "text";
    control = (
      <Input
        {...common}
        type={type}
        value={value}
        readOnly={readOnly}
        disabled={readOnly}
        autoFocus={field.key === "name"}
        onChange={(e) => onChange(e.target.value)}
        placeholder={PLACEHOLDERS[field.key]}
      />
    );
  }

  return (
    <div className={cn(wide && "sm:col-span-2")}>
      <Field
        label={field.label}
        htmlFor={id}
        required={field.required}
        helper={helper}
        error={error}
        errorId={errorId}
      >
        {control}
      </Field>
    </div>
  );
}

interface CustomFieldInputProps {
  field: LearnerFormCustomField;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
}

function CustomFieldInput({ field, value, error, onChange }: CustomFieldInputProps) {
  const id = fieldId(`custom_${field.key}`);
  const errorId = `${id}-error`;
  const common = {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? errorId : undefined,
  } as const;

  const control =
    field.type === "select" ? (
      <Select
        value={value || "__none__"}
        onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
      >
        <SelectTrigger {...common}>
          <SelectValue placeholder="Not specified" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Not specified</SelectItem>
          {field.options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : (
      <Input
        {...common}
        type={
          field.type === "email"
            ? "email"
            : field.type === "phone"
              ? "tel"
              : field.type === "date"
                ? "date"
                : "text"
        }
        inputMode={field.type === "number" ? "decimal" : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );

  return (
    <Field
      label={field.label}
      htmlFor={id}
      required={field.required}
      helper={field.help_text ?? undefined}
      error={error}
      errorId={errorId}
    >
      {control}
    </Field>
  );
}

interface SectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function Section({ title, subtitle, children }: SectionProps) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="mb-3">
        <h3 className="text-foreground text-sm font-semibold tracking-tight">{title}</h3>
        {subtitle ? <p className="text-muted-foreground mt-0.5 text-xs">{subtitle}</p> : null}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  error?: string;
  errorId?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, helper, error, errorId, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {error ? (
        <p id={errorId} className="text-destructive mt-1 text-xs">
          {error}
        </p>
      ) : helper ? (
        <p className="text-muted-foreground mt-1 text-xs">{helper}</p>
      ) : null}
    </div>
  );
}

function friendlyApiMessage(
  err: ApiError,
  personOne: string,
  ct: (text: string) => string,
): string {
  switch (err.code) {
    case "STUDENT_DUPLICATE_EMAIL":
      return `A ${personOne} with that email already exists in your institution.`;
    case "STUDENT_VALIDATION_FAILED":
      return err.message || "Some fields failed validation.";
    case "AZ_PERMISSION_DENIED":
      return ct("You don't have permission to save students. Ask an admin for access.");
    default:
      return err.message || `Couldn't save the ${personOne}. Please retry.`;
  }
}
